<template>
  <v-dialog v-if="spamTestEnabled && team.spam_test_enabled && form.html_body && template"
            v-model="dialog"
            max-width="90%"
            @click:outside="closeDialog">
    <template v-slot:activator="{ on, attrs }">
      <v-btn @click.prevent="checkSpam"
             :disabled="spamTestBusy"
             v-bind="attrs"
             v-on="on">
        <template v-if="spamTestBusy">
          <v-icon left
                  v-if="spamTestBusy">
            fal fa-fw fa-spin fa-spinner
          </v-icon>
          Checking for spam
        </template>
        <template v-else>
          Check for spam
        </template>
      </v-btn>
    </template>
    <v-card>
      <v-card-title>
        {{ spamTestBusy ? 'Checking for spam' : 'Spam Test Result' }}
        <v-spacer></v-spacer>
        <v-btn @click.prevent="closeDialog"
               icon>
          <v-icon>
            fal fa-times
          </v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <p>
          <a href="https://help.mobilelocker.com/article/201-how-to-check-an-email-template-for-spam" target="_blank">What is this?</a>
        </p>
        <v-alert v-if="errorMessage"
                 type="error"
                 tile
                 dense
                 :icon="false"
                 class="mb-0">
          <h3>Error</h3>
          {{ errorMessage }}
        </v-alert>
        <div v-if="spamTestBusy"
             class="text-center">
          <v-icon>
            fal fa-spin fa-spinner fa-5x
          </v-icon>
        </div>
        <v-simple-table v-if="hasResults">
          <template v-slot:default>
            <thead>
            <tr>
              <th class="text-left">
                Service
              </th>
              <th class="text-left">
                Score
              </th>
              <th class="text-left">
                Spam?
              </th>
              <th class="text-left">
                Description
              </th>
            </tr>
            </thead>
            <tbody>
            <template v-if="oop">
              <tr>
                <th class="text-left">
                  OOPSpam
                </th>
                <td>
                  {{ oop.score }}
                </td>
                <td>
                  <v-chip label
                          small
                          dark
                          :color="oop.isContentSpam ? 'error' : 'success'"
                          class="d-block text-center font-weight-bold text-uppercase">
                    {{ oop.isContentSpam ? 'Spam' : 'Not spam' }}
                  </v-chip>
                </td>
                <td>
                  A value between 0-6 representing an overall spam score. The higher this value, the more likely to be spam. While it depends on the use case, a score of 3 or higher can be considered spam.
                </td>
              </tr>
            </template>
            <template v-if="pm && pm.score !== -100">
              <tr>
                <th class="text-left">
                  SpamAssassin
                </th>
                <td>
                  {{ pm.score }}
                </td>
                <td>
                  <v-chip label
                          small
                          dark
                          :color="pm.score >= 3 ? 'error' : 'success'"
                          class="d-block text-center font-weight-bold text-uppercase">
                    {{ pm.score >= 3 ? 'Spam' : 'Not spam' }}
                  </v-chip>
                </td>
                <td>
                  A negative number is best, but a score between 0-2 is okay also. Read about <a href="https://www.mailercheck.com/articles/spamassassin-score" target="_blank">SpamAssassin scores</a>.
                </td>
              </tr>
            </template>
            <template v-if="sc">
              <tr>
                <th class="text-left">
                  SpamCheck.AI
                </th>
                <td>
                  {{ scScore }}
                </td>
                <td>
                  <v-chip label
                          small
                          dark
                          :color="sc.content?.spam?.result ? 'error' : 'success'"
                          class="d-block text-center font-weight-bold text-uppercase">
                    {{ sc.content?.spam?.result ? 'Spam' : 'Not spam' }}
                  </v-chip>
                </td>
                <td>
                  A score greater than 0.5 indicates spam.
                </td>
              </tr>
            </template>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>
      <v-card-actions class="px-4">
        <v-btn @click.prevent="closeDialog"
               :disabled="spamTestBusy">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import {mapGetters} from 'vuex';
import spamCheckService from '../../../services/spamCheckService';
import {isObject} from 'lodash';
import numeral from 'numeral'; // MLD-6390

export default {
  props: {
    form: {
      type: Object,
      required: true,
    },
    template: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      dialog: false,
      spamTestBusy: false,
      oop: null,
      pm: null,
      sc: null,
      errorMessage: null,
    }
  },
  computed: {
    ...mapGetters([
      'spamTestEnabled',
      'team',
      'user',
    ]),
    hasResults() {
      return isObject(this.oop)
        || isObject(this.pm)
        || isObject(this.sc);
    },
    scScore() { // https://chatgpt.com/c/25870f24-3362-4a52-9fa0-c4e04a37dc01
      if (this.sc && this.sc.content?.spam?.data?.score) {
        const score = this.sc.content.spam.data.score;
        /*const exponentialForm = score.toExponential(3);
        return Number(exponentialForm);*/
        return numeral(score).format('0[.]0000');
      }
      return null;
    }
  },
  methods: {
    closeDialog() {
      this.resetResults();
      this.errorMessage = null;
      this.spamTestBusy = false;
      this.dialog = false;
    },
    resetResults() {
      this.oop = null;
      this.pm = null;
      this.sc = null;
    },
    async checkSpam() { // MLD-4012
      if (!this.template) {
        return;
      }
      if (this.spamTestBusy) {
        return;
      }
      this.spamTestBusy = true;
      this.resetResults();
      this.errorMessage = null;
      const form = {
        html_body: this.form.html_body
      }
      try {
        await this.form.put(`/api/share-templates/${this.template.id}`, {data: form})
        this.oop = await spamCheckService.useOOP(this.template.id).catch(e => {
        });
        this.pm = await spamCheckService.usePM(this.template.id).catch(e => {
        });
        this.sc = await spamCheckService.useSC(this.template.id).catch(e => {
        });
      } catch (e) {
        if (e.response && e.response.data) {
          this.errorMessage = e.response.data.message;
        } else {
          this.errorMessage = 'There was an error';
        }
      } finally {
        this.spamTestBusy = false;
      }
    },
  }
}
</script>
